import pluralize from 'pluralize';
import { ItemDataType } from 'rsuite/esm/MultiCascadeTree';
import { LooseLicense } from 'src/types/licenses';

export enum TAX_FORMIK_PATH_ENUM {
  TAXABLE_PERIODS = 'taxablePeriods',
}

export enum TAX_PAYMENT_PERIOD_TYPES_ENUM {
  MONTHLY = 'monthly',
  MONTHLY_QUARTERLY = 'monthly_quarterly',
  NY_FISCAL = 'ny_fiscal_quarter',
  QUARTERLY = 'quarterly',
}

export enum RENTAL_STRUCTURE_STATUS_ENUM {
  HIDDEN = 'hidden',
  REQUIRED = 'required',
  OPTIONAL = 'optional',
}

export enum UNIT_NUMBER_STATUS_ENUM {
  HIDDEN = 'hidden',
  REQUIRED = 'required',
  OPTIONAL = 'optional',
}

export enum BREAKPOINT_ENUM {
  SMALL = 576,
  MEDIUM = 768,
}

export enum DASHBOARD_ACTIONS_ENUM {
  BULK_TAX = 'bulkTax',
  SINGLE_RENEW = 'singleRenew',
  BULK_RENEW = 'bulkRenew',
}

export enum FLOWS_ENUM {
  APPLY_LICENSE = 'applyLicense',
  UPDATE_CERTIFICATE = 'updateCertificate',
  ADMIN_UPDATE = 'adminUpdate',
  RENEW_LICENSE = 'renewLicense',
  BULK_RENEWAL = 'bulkRenewal',
  PAY_TOT = 'payTot',
  TBID = 'tbid',
  TAX_CERTIFICATE = 'taxCertificate',
}

export enum PAYMENT_METHODS {
  ACH_DIRECT_DEBIT = 'achDirectDebit',
  CREDIT_OR_DEBIT__CARD = 'creditOrDebitCard',
  CHECK = 'check',
}

export enum PAYMENT_METHOD_LABEL_ENUM {
  none = 'None',
  credit_card = 'Credit Card',
  ach = 'ACH',
  check = 'Check',
  push_ach = 'ACH',
  ach_credit_transfer = 'ACH',
}

export enum PERMIT_SORT_OPTIONS_ENUM {
  ADDRESS_ASC = 'addressAsc',
  ADDRESS_DESC = 'addressDesc',
  PERMIT_NUMBER_ASC = 'permitNumberAsc',
  PERMIT_NUMBER_DESC = 'permitNumberDesc',
  EXPIRATION_DATE_ASC = 'expirationDateAsc',
  EXPIRATION_DATE_DESC = 'expirationDateDesc',
  OWNER_NAME_ASC = 'ownerNameAsc',
  OWNER_NAME_DESC = 'ownerNameDesc',
  STATUS_ASC = 'statusAsc',
  STATUS_DESC = 'statusDesc',
}

export enum INPUT_TYPE {
  NUMBER = 'number',
  TEXT = 'text',
  TEXTAREA = 'textarea',
}

export enum FORM_INPUT_TYPES {
  EMAIL = 'email',
  NUMBER = 'number',
  CURRENCY = 'currency',
  PHONE = 'phone',
  TEXT = 'text',
}

export enum MULTIPLE_PERMITS_PER_APN_POLICY_OPTIONS_ENUM {
  BLOCK = 'block',
  WARN = 'warn',
  ALLOW = 'allow',
  WARN_AND_SHOW_LICENSE_DETAILS = 'warn_and_show_license_details',
  BLOCK_AND_SHOW_LICENSE_DETAILS = 'block_and_show_license_details',
}

export enum ALL_PERMITS_STATUS_OPTIONS_ENUM {
  ALL = 'all',
  ACTIVE = 'Active',
  INACTIVE = 'inactive',
}

export enum ADMIN_UPDATE_ACCOUNT_OPTIONS_ENUM {
  NEWACCOUNT = 'newAccount',
  MOVETOEXISTINGACCOUNT = 'moveToExistingAccount',
  NOCHANGE = 'noChange',
}

// Licenses Filters
export const LICENCES_SORT_CONFIG: Record<
  PERMIT_SORT_OPTIONS_ENUM,
  { key: keyof LooseLicense; order: 'asc' | 'desc' }
> = {
  [PERMIT_SORT_OPTIONS_ENUM.ADDRESS_ASC]: { key: 'address', order: 'asc' },
  [PERMIT_SORT_OPTIONS_ENUM.ADDRESS_DESC]: {
    key: 'address',
    order: 'desc',
  },
  [PERMIT_SORT_OPTIONS_ENUM.PERMIT_NUMBER_ASC]: {
    key: 'permitNumber',
    order: 'asc',
  },
  [PERMIT_SORT_OPTIONS_ENUM.PERMIT_NUMBER_DESC]: {
    key: 'permitNumber',
    order: 'desc',
  },
  [PERMIT_SORT_OPTIONS_ENUM.STATUS_ASC]: { key: 'status', order: 'asc' },
  [PERMIT_SORT_OPTIONS_ENUM.STATUS_DESC]: { key: 'status', order: 'desc' },
  [PERMIT_SORT_OPTIONS_ENUM.EXPIRATION_DATE_ASC]: {
    key: 'expirationDate',
    order: 'asc',
  },
  [PERMIT_SORT_OPTIONS_ENUM.EXPIRATION_DATE_DESC]: {
    key: 'expirationDate',
    order: 'desc',
  },
  [PERMIT_SORT_OPTIONS_ENUM.OWNER_NAME_ASC]: {
    key: 'ownersNames',
    order: 'asc',
  },
  [PERMIT_SORT_OPTIONS_ENUM.OWNER_NAME_DESC]: {
    key: 'ownersNames',
    order: 'desc',
  },
};

export const LICENSES_STATUS_OPTIONS = (
  registrationTypeVerbiage: string
): ItemDataType<ALL_PERMITS_STATUS_OPTIONS_ENUM>[] => [
  {
    name: 'All Statuses',
    value: ALL_PERMITS_STATUS_OPTIONS_ENUM.ALL,
  },
  {
    name: `Active  ${pluralize(registrationTypeVerbiage)}`,
    value: ALL_PERMITS_STATUS_OPTIONS_ENUM.ACTIVE,
  },
  {
    name: `Inactive ${pluralize(registrationTypeVerbiage)}`,
    value: ALL_PERMITS_STATUS_OPTIONS_ENUM.INACTIVE,
  },
];

export const LICENSES_SORT_OPTIONS = (
  registrationTypeVerbiage: string,
  ownerLabel: string
): ItemDataType<PERMIT_SORT_OPTIONS_ENUM>[] => [
  {
    name: 'Address, A -> Z',
    value: PERMIT_SORT_OPTIONS_ENUM.ADDRESS_ASC,
  },
  {
    name: 'Address, Z -> A',
    value: PERMIT_SORT_OPTIONS_ENUM.ADDRESS_DESC,
  },
  {
    name: `${registrationTypeVerbiage} Number, smallest to biggest`,
    value: PERMIT_SORT_OPTIONS_ENUM.PERMIT_NUMBER_ASC,
  },
  {
    name: `${registrationTypeVerbiage} Number, biggest to smallest`,
    value: PERMIT_SORT_OPTIONS_ENUM.PERMIT_NUMBER_DESC,
  },
  {
    name: `${registrationTypeVerbiage} Status, A -> Z`,
    value: PERMIT_SORT_OPTIONS_ENUM.STATUS_ASC,
  },
  {
    name: `${registrationTypeVerbiage} Status, Z -> A`,
    value: PERMIT_SORT_OPTIONS_ENUM.STATUS_DESC,
  },
  {
    name: `Expiration Date, soonest first`,
    value: PERMIT_SORT_OPTIONS_ENUM.EXPIRATION_DATE_DESC,
  },
  {
    name: `Expiration Date, furthest first`,
    value: PERMIT_SORT_OPTIONS_ENUM.EXPIRATION_DATE_ASC,
  },
  {
    name: `${pluralize(ownerLabel)} Name, A -> Z`,
    value: PERMIT_SORT_OPTIONS_ENUM.OWNER_NAME_ASC,
  },
  {
    name: `${pluralize(ownerLabel)} Name, Z -> A`,
    value: PERMIT_SORT_OPTIONS_ENUM.OWNER_NAME_DESC,
  },
];
